import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import * as XLSX from 'xlsx'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnFiltersState,
  SortingState,
  PaginationState,
  FilterFn,
  ColumnDef,
  Column,
  Table,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Switch } from '@progress/kendo-react-inputs'
import { getPiecesGridData } from '../../data/piecesData'
import { piecesGrid, purchaseOrderMatch, worktypesGrid, dropDown } from '../../data/interfaces'
import { getPurchaseOrderMatches } from '../../data/purchaseOrderData'
import { DateFormatter } from '../../data/helper'
import dayjs from 'dayjs'
import { getWorktypes } from '../../data/worktypeData'
import { getXeroDepartmentsDropdown, getXeroDepartments, getXeroSuppliersDropdown, XeroDepartmentFull } from '../../data/xeroData'
import { getCurrentUserEmployee } from '../../data/employeeData'
import './Grids.css'
import { 
  FaEye, 
  FaEdit, 
  FaTrash, 
  FaFlag, 
  FaPlusCircle, 
  FaFileExcel, 
  FaSortAmountUpAlt, 
  FaSortAmountDown, 
  FaSort,
  FaLink,
  FaLock,
  FaSearchDollar
} from 'react-icons/fa'
import { useToast } from '../../contexts/ToastContext'

declare module '@tanstack/table-core' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
    numberRange: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({
    itemRank,
  })
  return itemRank.passed
}

const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  const [min, max] = value || [null, null]
  const rowValue = Number(row.getValue(columnId))
  
  if (min !== null && max !== null) {
    return rowValue >= min && rowValue <= max
  } else if (min !== null) {
    return rowValue >= min
  } else if (max !== null) {
    return rowValue <= max
  }
  return true
}

const booleanFilter: FilterFn<any> = (row, columnId, value) => {
  if (value === null || value === undefined) return true
  return row.getValue(columnId) === value
}

interface PiecesGridProps {
  jobId?: number
  departmentId?: number
  showFlaggedOnly?: boolean
  refreshTrigger?: number
  lockEdit?: boolean
  onEditToggle?: (pieceId: number) => void
  onPiecesListUpdate?: (pieces: number[]) => void
}

function JobReferenceCell({ row }: { row: piecesGrid }) {
  const [mouseTooltip, setMouseTooltip] = useState<{ x: number; y: number; text: string } | null>(null)
  const tooltipText = row.comments || row.jobReference || row.projectName

  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
      <span
        className="overflow__container"
        style={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: "5px" }}
        onMouseEnter={(e) => { if (tooltipText) setMouseTooltip({ x: e.clientX, y: e.clientY, text: tooltipText }) }}
        onMouseMove={(e) => { if (tooltipText) setMouseTooltip({ x: e.clientX, y: e.clientY, text: tooltipText }) }}
        onMouseLeave={() => setMouseTooltip(null)}
      >
        {row.jobReference || row.projectName || '-'}
      </span>
      {mouseTooltip && (
        <div style={{
          position: "fixed",
          left: mouseTooltip.x + 12,
          top: mouseTooltip.y - 28,
          background: "#333",
          color: "#fff",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          pointerEvents: "none",
          zIndex: 99999,
          maxWidth: "400px",
          whiteSpace: "pre-wrap",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
        }}>
          {mouseTooltip.text}
        </div>
      )}
      {row.flagged && (
        <span style={{ marginLeft: "10px" }}>
          <Tooltip anchorElement="target" position="top">
            <FaFlag fill={"#e9540d"} title="Flagged Piece" style={{ fontSize: "12pt" }} />
          </Tooltip>
        </span>
      )}
    </div>
  )
}

function PiecesGrid(props: PiecesGridProps) {
  const dateFormatter = new DateFormatter()
  const [allData, setAllData] = useState<piecesGrid[]>([]) // Store all data for filtering
  const [loading, setLoading] = useState(true)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [worktypes, setWorktypes] = useState<worktypesGrid[]>([])
  const [xeroDepartments, setXeroDepartments] = useState<dropDown[]>([])
  const [xeroDepartmentsFull, setXeroDepartmentsFull] = useState<XeroDepartmentFull[]>([])
  const [xeroSuppliers, setXeroSuppliers] = useState<dropDown[]>([])
  const [selectedWorktype, setSelectedWorktype] = useState<worktypesGrid | null>(null)
  const [showFlagged, setShowFlagged] = useState(false)
  const [showSubmitted, setShowSubmitted] = useState<boolean | null>(null) // null = all, true = submitted only, false = not submitted only
  const { showToast } = useToast()

  // Purchase order matches, keyed by piece, so the grid can flag a row without
  // a request per row. Loaded alongside the pieces themselves.
  const [purchaseOrderMatches, setPurchaseOrderMatches] = useState<Record<number, purchaseOrderMatch>>({})

  // Create a lookup map for Xero departments
  const xeroDepartmentLookup = useMemo(() => {
    const lookup: { [key: number]: string } = {}
    xeroDepartments.forEach(dept => {
      lookup[dept.value] = dept.text
    })
    return lookup
  }, [xeroDepartments])

  // Xero supplier names by id, archived ones included, so older pieces still show theirs
  const xeroSupplierLookup = useMemo(() => {
    const lookup: { [key: number]: string } = {}
    xeroSuppliers.forEach(supplier => {
      lookup[supplier.value] = supplier.text
    })
    return lookup
  }, [xeroSuppliers])

  // Create a mapping from worktypeId to Xero department IDs for filtering
  const worktypeToXeroDepartments = useMemo(() => {
    const mapping: { [worktypeId: number]: number[] } = {}
    
    if (xeroDepartmentsFull.length === 0) {
      return mapping
    }
    
    xeroDepartmentsFull.forEach((xeroDept) => {
      const deptId = (xeroDept as any).xeroTrackingDepartmentId
      const worktypeId = xeroDept.worktypeId ?? xeroDept.WorktypeId
      
      if (deptId !== undefined && deptId !== null) {
        if (worktypeId !== undefined && worktypeId !== null && worktypeId !== 0) {
          if (!mapping[worktypeId]) {
            mapping[worktypeId] = []
          }
          mapping[worktypeId].push(deptId)
        }
      }
    })
    
    return mapping
  }, [xeroDepartmentsFull])

  const exportToExcel = () => {
    const filtered = table.getFilteredRowModel().rows.map(r => r.original)
    
    const headers = [
      'piecesId', 'xeroAccount', 'projectName', 'supplier', 'invoiceId', 'invoiceDate', 'dueDate', 'total', 'comments'
    ]

    let date = new Date().toDateString()
    date = dateFormatter.format(date)

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(filtered, { header: headers })

    XLSX.utils.book_append_sheet(wb, ws, 'Pieces')
    const fileName = `pieces_${dateFormatter.format(new Date().toDateString())}.xlsx`
    XLSX.writeFile(wb, fileName)
  }

  const columns = useMemo<ColumnDef<piecesGrid>[]>(
    () => [
      
      {
        accessorKey: 'jobReference',
        header: 'Job No',
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => (
          <JobReferenceCell row={row.original} />
        ),
      },
      {
        accessorKey: 'siteAddress',
        header: 'Site',
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => (
          <Tooltip anchorElement="target" position="top">
            <span
              title={(row.original as any).siteAddress || 'No site assigned'}
              className="overflow__container"
            >
              {(row.original as any).siteAddress || '-'}
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'worktypeName',
        header: 'Department',
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => {
          // Use Xero department name if available, otherwise fall back to worktype
          const xeroDeptId = row.original.xeroTrackingDepartmentId
          const xeroDeptName = xeroDeptId && xeroDeptId > 0 ? xeroDepartmentLookup[xeroDeptId] : null
          const departmentDisplay = xeroDeptName || row.original.worktypeName
          
          return (
            <Tooltip anchorElement="target" position="top">
              <span
                title={departmentDisplay}
                className="overflow__container"
              >
                {departmentDisplay || '-'}
              </span>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'supplierName',
        header: 'Supplier',
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => (
          <Tooltip anchorElement="target" position="top">
            <span
              title={row.original.supplierName}
              className="overflow__container"
            >
              {row.original.supplierName}
            </span>
          </Tooltip>
        ),
      },
      {
        // The name, not the id, so filtering and sorting go by what's shown.
        id: 'xeroSupplier',
        accessorFn: (row: piecesGrid) => (row.xeroSupplierId ? xeroSupplierLookup[row.xeroSupplierId] ?? '' : ''),
        header: 'Xero Supplier',
        footer: (props) => props.column.id,
        cell: ({ getValue }) => {
          const name = getValue() as string
          return (
            <Tooltip anchorElement="target" position="top">
              <span title={name} className="overflow__container">
                {name || '-'}
              </span>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'subTotal',
        header: 'Sub Total',
        enableColumnFilter: false,
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => 
          `£${row.original.subTotal || '0.00'}`,
      },
      {
        accessorKey: 'invoiceId',
        header: 'Invoice ID',
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'invoiceDate',
        header: 'Invoice Date',
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => {
          if (!row.original.invoiceDate) return '-'
          
          // Handle non-standard format like "Oct  6 2025 11:00PM"
          const dateStr = row.original.invoiceDate
          const normalized = dateStr.replace(/\s+/g, ' ').trim()
          const dateOnly = normalized.split(' ').slice(0, 3).join(' ') // Extract "Oct 6 2025"
          
          const parsed = dayjs(dateOnly)
          return parsed.isValid() ? parsed.format('DD-MM-YYYY') : '-'
        },
      },
      {
        id: 'purchaseOrderMatch',
        accessorFn: (row: piecesGrid) => {
          const match = purchaseOrderMatches[row.piecesId]
          if (!match) return 'None'
          if (match.isAllocated) return match.allocationLocked ? 'Sent to Xero' : 'Allocated'
          return match.hasMatch ? 'Match found' : 'None'
        },
        header: 'Purchase Order',
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: piecesGrid } }) => {
          const match = purchaseOrderMatches[row.original.piecesId]

          if (!match) {
            return <span style={{ color: '#8a8a8a' }}>-</span>
          }

          // Already allocated: show which order, and whether that pairing has
          // been fixed by going to Xero.
          if (match.isAllocated) {
            return (
              <Tooltip anchorElement="target" position="top">
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgb(94 142 23)', fontWeight: 600 }}
                  title={
                    match.allocationLocked
                      ? `Allocated to ${match.orderNo} and sent to Xero - this cannot be reallocated`
                      : `Allocated to ${match.orderNo}`
                  }
                >
                  {match.allocationLocked ? <FaLock /> : <FaLink />}
                  {match.orderNo}
                </span>
              </Tooltip>
            )
          }

          // Not allocated yet, but a published order matches the reference on
          // the piece - the user still has to confirm it.
          if (match.hasMatch) {
            return (
              <Tooltip anchorElement="target" position="top">
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#b06a00', fontWeight: 600 }}
                  title={`Purchase order ${match.orderNo} matches this invoice - open the piece to allocate it`}
                >
                  <FaSearchDollar />
                  {match.orderNo}
                </span>
              </Tooltip>
            )
          }

          return <span style={{ color: '#8a8a8a' }}>-</span>
        },
      },
      {
        accessorKey: 'submitToXero',
        header: 'Submitted',
        footer: (props) => props.column.id,
        enableColumnFilter: false,
        cell: ({ row }: { row: { original: piecesGrid } }) => (
          <div style={{ textAlign: 'center' }}>
            {row.original.submitToXero ? (
              <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓</span>
            ) : (
              <span style={{ color: '#dc3545', fontWeight: 'bold' }}>✗</span>
            )}
          </div>
        ),
      },
      
    ],
    [xeroDepartmentLookup, xeroSupplierLookup, purchaseOrderMatches] // Re-create columns when the lookups change
  )

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      let result: piecesGrid[] = []
      
      if (props.showFlaggedOnly) {
        const { getFlaggedPieces } = await import('../../data/piecesData')
        result = await getFlaggedPieces()
      } else if (props.jobId) {
        const { getPiecesByJob } = await import('../../data/piecesData')
        result = await getPiecesByJob(props.jobId)
      } else if (props.departmentId) {
        const { getPiecesByDepartment } = await import('../../data/piecesData')
        result = await getPiecesByDepartment(props.departmentId)
      } else {
        result = await getPiecesGridData()
      }
      
      setAllData(result) // Store all data for filtering

      // A failed match lookup shouldn't cost the user their pieces list, so it
      // only clears the indicator column.
      try {
        const matches = await getPurchaseOrderMatches()
        setPurchaseOrderMatches(
          matches.reduce((lookup: Record<number, purchaseOrderMatch>, match) => {
            lookup[match.piecesId] = match
            return lookup
          }, {})
        )
      } catch (matchError) {
        console.error('Error fetching purchase order matches:', matchError)
        setPurchaseOrderMatches({})
      }
    } catch (error) {
      console.error('Error fetching pieces data:', error)
      showToast('Failed to load pieces data', 'error')
    } finally {
      setLoading(false)
    }
  }, [props.jobId, props.departmentId, props.showFlaggedOnly, selectedWorktype, showFlagged, showSubmitted])





  // Fetch worktypes on component mount
  useEffect(() => {
    const fetchWorktypes = async () => {
      try {
        const worktypeData = await getWorktypes()
        // Add "All" option at the beginning
        const allOption: worktypesGrid = { worktypeId: -1, group: '', worktype: 'All' }
        const worktypesWithAll = [allOption, ...worktypeData]
        setWorktypes(worktypesWithAll)

        // Get current user's worktype default
        const currentUser = await getCurrentUserEmployee()
        
        if (currentUser && currentUser.worktypeDefaultId) {
          // Find the user's default worktype in the list
          const userDefaultWorktype = worktypesWithAll.find(wt => wt.worktypeId === currentUser.worktypeDefaultId)
          
          if (userDefaultWorktype) {
            setSelectedWorktype(userDefaultWorktype)
          } else {
            setSelectedWorktype(allOption) // Fallback to "All" if user's worktype not found
          }
        } else {
          setSelectedWorktype(allOption) // Default to "All" if no user worktype specified
        }
      } catch (error) {
        console.error('Error fetching worktypes:', error)
        showToast('Failed to load departments', 'error')
      }
    }
    fetchWorktypes()
  }, [])

  useEffect(() => {
    const fetchXeroDepartments = async () => {
      try {
        const xeroDeptData = await getXeroDepartmentsDropdown()
        setXeroDepartments(xeroDeptData)
      } catch (error) {
        console.error('Error fetching Xero departments:', error)
        // Don't show error toast as this is optional data
      }
    }
    fetchXeroDepartments()
  }, [])

  useEffect(() => {
    const fetchXeroDepartmentsFull = async () => {
      try {
        const xeroDeptFullData = await getXeroDepartments()
        setXeroDepartmentsFull(xeroDeptFullData)
      } catch (error) {
        console.error('Error fetching full Xero departments data:', error)
        // Don't show error toast as this is optional data
      }
    }
    fetchXeroDepartmentsFull()
  }, [])

  useEffect(() => {
    const fetchXeroSuppliers = async () => {
      try {
        const xeroSupplierData = await getXeroSuppliersDropdown()
        setXeroSuppliers(xeroSupplierData)
      } catch (error) {
        console.error('Error fetching Xero suppliers:', error)
        // Don't show error toast as this is optional data
      }
    }
    fetchXeroSuppliers()
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData, props.refreshTrigger])



  // Create filtered data based on local state filters
  const filteredData = useMemo(() => {
    let result = allData

    // Filter by worktype/Xero department
    if (selectedWorktype && selectedWorktype.worktypeId !== -1) {
      const xeroDeptIdsForWorktype = worktypeToXeroDepartments[selectedWorktype.worktypeId] || []
      
      result = result.filter(item => {
        const xeroDeptId = item.xeroTrackingDepartmentId
        
        // If the piece has a Xero department assigned, check if it belongs to the selected worktype
        if (xeroDeptId !== null && xeroDeptId !== undefined && xeroDeptId > 0) {
          const deptIdNum = Number(xeroDeptId)
          const expectedIds = xeroDeptIdsForWorktype.map(id => Number(id))
          return expectedIds.includes(deptIdNum)
        }
        // If no Xero department, fall back to traditional worktype filtering
        return item.worktypeId === selectedWorktype.worktypeId
      })
    }

    // Then filter by flagged status if enabled
    if (showFlagged) {
      result = result.filter(item => item.flagged === true)
    }

    // Filter by submitted status if specified
    if (showSubmitted !== null) {
      if (showSubmitted === true) {
        // Show only submitted pieces
        result = result.filter(item => item.submitToXero === true)
      } else if (showSubmitted === false) {
        // Show not submitted pieces (including null values)
        result = result.filter(item => item.submitToXero === false || item.submitToXero === null)
      }
    }

    return result
  }, [allData, selectedWorktype, showFlagged, showSubmitted, worktypeToXeroDepartments])

  const table = useReactTable({
    data: filteredData, // Use filtered data for the table
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  })

  // Update parent with current pieces list when filtered/sorted data changes
  useEffect(() => {
    if (props.onPiecesListUpdate) {
      // Use the table's sorted row model to get data after filtering AND sorting
      const tableSortedRows = table.getSortedRowModel().rows
      const pieceIds = tableSortedRows.map((row) => row.original.piecesId)
      props.onPiecesListUpdate(pieceIds)
    }
  }, [filteredData, columnFilters, globalFilter, sorting, props.onPiecesListUpdate, table])

  if (loading) {
    return <div className="loading">Loading pieces data...</div>
  }

  return (
    <div className="p-2">
      {/* This is the global column search box */}
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <div>Pieces</div>
            </div>     
       
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "1rem"}}>
              <div style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                <Switch
                  checked={showFlagged}
                  onChange={(e) => {
                    setShowFlagged(e.value)
                  }}
                />
                <label style={{ fontSize: "12pt", color: "white" }}>Flagged only</label>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <DropDownList
                  data={[
                    { text: 'All', value: null },
                    { text: 'Submitted only', value: true },
                    { text: 'Not submitted', value: false }
                  ]}
                  textField="text"
                  dataItemKey="value"
                  value={[
                    { text: 'All', value: null },
                    { text: 'Submitted only', value: true },
                    { text: 'Not submitted', value: false }
                  ].find(item => item.value === showSubmitted)}
                  onChange={(e) => {
                    setShowSubmitted(e.value.value)
                  }}
                  style={{ width: "150px" }}
                />
                <label style={{ fontSize: "12pt", color: "white" }}>Submission Status</label>
              </div>

              <DropDownList
                data={worktypes}
                textField="worktype"
                dataItemKey="worktypeId"
                value={selectedWorktype}
                onChange={(e) => {
                  setSelectedWorktype(e.value)
                }}
                style={{ width: "200px" }}
              />
              
              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Export pieces</p>
              </div>
            </div>
          </div>
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            placeholder="Search all piece columns..."
            className="filter__input__global"
          />
        </div>
      </div>

            <div className="h-2" />
      <table className="grid__table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <>
                        {/* This DIV is where you hover/click on the column header to sort */}
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "header__sortable"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: (
                              <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                            ),
                            desc: (
                              <FaSortAmountDown style={{ fontSize: "14pt" }} />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <FaSort style={{ fontSize: "14pt" }} />
                          )}
                        </div>

                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const bgColor = () => {
              if (row.original.flagged) {
                return "rgba(255, 235, 59, 0.2)"; // Light yellow for flagged
              } else {
                return "rgba(245, 249, 251, 1)";
              }
            };

            return (
              <tr
                onDoubleClick={() => {
                  if (props.lockEdit) {
                    showToast('Edit functionality is disabled', 'info');
                  } else if (props.onEditToggle && row.original.piecesId) {
                    props.onEditToggle(row.original.piecesId);
                  } else {
                    showToast('Edit functionality coming soon', 'info');
                  }
                }}
                style={{ backgroundColor: bgColor(), cursor: 'pointer' }}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* This is the pagination controls */}
      <div className="page__container">
        <div className="page__button__container">
          <button
            className="page__button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="page__button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
        </div>

        <div className="page__controls">
          <div className="page__numbers">
            <div>Page&nbsp;</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </div>
          <div className="page__numbers__goto">
            <div className="page__numbers__gototext">| Go to page:</div>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="filter__input"
            />
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="page__select"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any, index: number) => (
          <option value={value} key={`${column.id}-${index}-${value}`} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export default PiecesGrid;